import { useMemo, useState } from 'react';
import type { Vendor } from '@stfors/shared-types';
import { useVendors, vendorDisplayName } from '../api/queries/vendors';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { VendorFormModal } from './VendorFormModal';
import './VendorPicker.css';

interface VendorPickerProps {
  /** Currently selected vendor id, or null when nothing is picked. */
  value: number | null;
  /** Fires whenever the selection changes — also fires with a freshly
   *  created vendor's id after the user adds one from the inline modal. */
  onChange: (vendorId: number | null, vendor: Vendor | null) => void;
  /** If `vendor.write` is denied, hide the "+ Add new" affordance. */
  canAdd?: boolean;
  /** Show a default-tax-category hint underneath the picker. */
  showTaxCategoryHint?: boolean;
  disabled?: boolean;
  /**
   * Opt in to name-only answers: a "Generic" group from the `generic_vendor`
   * lookup (migration 0051) plus a "use what I typed" row, for spend that
   * shouldn't leave a vendor record behind — a tank of gas, a lunch. Requires
   * `textValue` + `onTextChange`; without it the picker behaves exactly as it
   * always has.
   */
  allowGeneric?: boolean;
  /** The current name-only selection ('' when a real vendor or nothing is picked). */
  textValue?: string;
  onTextChange?: (name: string) => void;
}

/**
 * Picker for an expense's vendor. Implemented as a typeahead text input over the
 * vendor list (filtered client-side from a single fetch), with a button that
 * opens the structured VendorFormModal. After a successful create we propagate
 * the new vendor's id up via onChange so the parent form lands selected on the
 * row it just made.
 *
 * With `allowGeneric` it can return one of two mutually exclusive things:
 *
 *   - a vendor id     → `onChange(id, vendor)`, `onTextChange('')`
 *   - a bare name     → `onChange(null, null)`, `onTextChange(name)`
 *
 * which is exactly the `vendor_id` XOR `vendor` shape the expenses API already
 * expects (`expenses.vendor` is a name snapshot; `vendor_id` a nullable soft FK).
 */
export function VendorPicker({
  value,
  onChange,
  canAdd = true,
  showTaxCategoryHint = false,
  disabled = false,
  allowGeneric = false,
  textValue = '',
  onTextChange,
}: VendorPickerProps): JSX.Element {
  const vendorsQ = useVendors();
  const vendors = vendorsQ.data?.vendors ?? [];

  // Only fetch the generic vocabulary where it's actually offered — most
  // pickers in the app don't need it.
  const genericQ = useLookups('generic_vendor', { enabled: allowGeneric });
  const generics = allowGeneric
    ? activeOptionsFor(genericQ.data?.lookup_options, 'generic_vendor')
    : [];

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const selected = useMemo(
    () => vendors.find((v) => v.id === value) ?? null,
    [vendors, value],
  );
  // A name-only pick only counts while no real vendor is selected — the two
  // are mutually exclusive, and the vendor always wins.
  const genericName = selected === null ? textValue.trim() : '';

  // What appears in the input. When the dropdown is closed we show the current
  // selection (vendor name, or the generic/one-off name); when open we let the
  // user type a search query. Keeping these in two states avoids a stale-input
  // bug where re-opening would lose what they were typing.
  const inputValue = open
    ? query
    : selected
      ? vendorDisplayName(selected)
      : genericName;

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (q.length === 0) return vendors;
    return vendors.filter((v) => {
      const haystack = `${v.name} ${v.dba_name ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [vendors, q]);

  const filteredGenerics = useMemo(
    () => (q.length === 0 ? generics : generics.filter((g) => g.value.toLowerCase().includes(q))),
    [generics, q],
  );

  // Offer "use what I typed" only when it isn't already on offer verbatim —
  // otherwise every exact match shows the same name twice.
  const typedIsNovel =
    allowGeneric
    && query.trim().length > 0
    && !generics.some((g) => g.value.toLowerCase() === q)
    && !vendors.some((v) => v.name.toLowerCase() === q);

  const close = () => {
    setQuery('');
    setOpen(false);
  };

  const choose = (v: Vendor | null) => {
    onChange(v?.id ?? null, v);
    // Picking a real vendor (or clearing) retires any name-only answer.
    onTextChange?.('');
    close();
  };

  /** Commit a name with no vendor record behind it. */
  const chooseName = (name: string) => {
    onChange(null, null);
    onTextChange?.(name);
    close();
  };

  return (
    <div className={`vp-root${disabled ? ' vp-disabled' : ''}`}>
      <div className="vp-input-wrap">
        <input
          type="text"
          className="form-input vp-input"
          value={inputValue}
          placeholder={
            vendorsQ.isPending
              ? 'Loading vendors…'
              : allowGeneric ? 'Search, or pick a category…' : 'Search vendors…'
          }
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Defer so clicks on the dropdown register first.
            setTimeout(() => setOpen(false), 120);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            // Enter commits what you typed rather than submitting the form
            // around us — a half-finished expense row shouldn't save because
            // someone finished typing "Sheetz".
            if (e.key === 'Enter' && typedIsNovel) {
              e.preventDefault();
              chooseName(query.trim());
            }
          }}
          disabled={disabled || vendorsQ.isPending}
          aria-label="Vendor"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {(selected || genericName) && !open && (
          <button
            type="button"
            className="vp-clear"
            onClick={() => choose(null)}
            aria-label="Clear vendor"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>

      {/* A name-only pick is not a link to anything, and that difference
          matters when you later wonder why a vendor page has no history. */}
      {genericName && !open && (
        <p className="vp-generic-note">No vendor record — recorded as a name only.</p>
      )}

      {open && (
        <ul className="vp-options" role="listbox">
          {typedIsNovel && (
            <li role="option" aria-selected={false}>
              <button
                type="button"
                className="vp-option vp-option-typed"
                onMouseDown={(e) => e.preventDefault() /* keep focus */}
                onClick={() => chooseName(query.trim())}
              >
                Use &ldquo;{query.trim()}&rdquo; as a one-off
              </button>
            </li>
          )}

          {filteredGenerics.length > 0 && (
            <>
              <li className="vp-group" role="presentation">Generic — no vendor record</li>
              {filteredGenerics.map((g) => (
                <li key={`generic-${g.id}`} role="option" aria-selected={g.value === genericName}>
                  <button
                    type="button"
                    className="vp-option"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => chooseName(g.value)}
                  >
                    <span className="vp-option-name">{g.value}</span>
                  </button>
                </li>
              ))}
            </>
          )}

          {filtered.length === 0 && filteredGenerics.length === 0 && !typedIsNovel && (
            <li className="vp-empty">
              {query.trim().length > 0 ? `No vendors match "${query}"` : 'No vendors yet'}
            </li>
          )}

          {filtered.length > 0 && (
            <>
              {allowGeneric && <li className="vp-group" role="presentation">Vendors</li>}
              {filtered.map((v) => (
                <li key={v.id} role="option" aria-selected={v.id === value}>
                  <button
                    type="button"
                    className="vp-option"
                    onMouseDown={(e) => e.preventDefault() /* keep focus */}
                    onClick={() => choose(v)}
                  >
                    <span className="vp-option-name">{v.name}</span>
                    {v.dba_name && v.legal_name && (
                      <span className="vp-option-dba"> legal: {v.legal_name}</span>
                    )}
                    {v.city && v.state && (
                      <span className="vp-option-loc">{v.city}, {v.state}</span>
                    )}
                  </button>
                </li>
              ))}
            </>
          )}

          {canAdd && (
            <li className="vp-add-row">
              <button
                type="button"
                className="vp-add"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
              >
                + Add new vendor…
              </button>
            </li>
          )}
        </ul>
      )}

      {showTaxCategoryHint && selected?.default_tax_category && (
        <p className="vp-hint">
          Default tax category: <strong>{selected.default_tax_category}</strong>
        </p>
      )}

      <VendorFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(v) => choose(v)}
      />
    </div>
  );
}
